import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface AccordionCtx {
  openId: string | null;
  setOpenId: (id: string | null) => void;
}

const Ctx = createContext<AccordionCtx | null>(null);

interface AccordionGroupProps {
  children: ReactNode;
  /** ID de la sección que arranca abierta. Si no se provee, todas arrancan cerradas. */
  initialOpen?: string | null;
}

/**
 * Contenedor de acordeón **exclusivo**: sólo una sección puede estar abierta
 * a la vez. Cuando el user abre una, la anterior se cierra automáticamente.
 * Esto evita que la ventana crezca verticalmente al infinito.
 */
export function AccordionGroup({ children, initialOpen = null }: AccordionGroupProps) {
  const [openId, setOpenId] = useState<string | null>(initialOpen);
  return (
    <Ctx.Provider value={{ openId, setOpenId }}>
      <div className="flex flex-col gap-2">{children}</div>
    </Ctx.Provider>
  );
}

interface AccordionSectionProps {
  /** Identificador único dentro del grupo. */
  id: string;
  icon?: ReactNode;
  title: string;
  /** Resumen chico a la derecha del título cuando la sección está cerrada. */
  summary?: ReactNode;
  /** Meta-info siempre visible (abierto y cerrado), a la derecha del título. */
  meta?: ReactNode;
  children: ReactNode;
}

/**
 * Sección de acordeón. Al abrirse, hace scroll suave para dejar su header en
 * la parte superior del scroll-container del Drawer — así el campo a editar
 * queda arriba en la vista sin necesidad de scrollear a mano.
 */
export function AccordionSection({
  id,
  icon,
  title,
  summary,
  meta,
  children,
}: AccordionSectionProps) {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('AccordionSection fuera de AccordionGroup');

  const open = ctx.openId === id;
  const rootRef = useRef<HTMLDivElement>(null);

  // Al pasar a abierto: dejamos que la animación arranque y llevamos el header
  // a la parte superior del scroll ancestor (en nuestro caso el Drawer body).
  useEffect(() => {
    if (!open || !rootRef.current) return;
    // Buscamos el ancestor con overflow-y (Drawer body) para scroll focal.
    const el = rootRef.current;
    const scrollParent = findScrollParent(el);
    if (!scrollParent) return;
    // Pequeño delay para que la transición no compita con el scroll.
    const t = setTimeout(() => {
      const elTop = el.getBoundingClientRect().top;
      const parentTop = scrollParent.getBoundingClientRect().top;
      const delta = elTop - parentTop;
      scrollParent.scrollBy({ top: delta - 8, behavior: 'smooth' });
    }, 160);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <div ref={rootRef} className="rounded-md border border-surface-line bg-white">
      <button
        type="button"
        onClick={() => ctx.setOpenId(open ? null : id)}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition',
          open ? 'border-b border-surface-line bg-blue-10/20' : 'hover:bg-blue-10/30',
        )}
        aria-expanded={open}
      >
        {icon && <span className="flex-none text-blue">{icon}</span>}
        <span className="flex-1 text-sm font-semibold text-neutral-ink">{title}</span>
        {meta && <span className="text-neutral-faint truncate text-xs">{meta}</span>}
        {summary && !open && (
          <span className="truncate text-xs text-neutral-muted">{summary}</span>
        )}
        <ChevronDown
          size={16}
          className={cn(
            'flex-none text-neutral-muted transition-transform',
            open && 'rotate-180 text-blue',
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-200 ease-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY)) return node;
    node = node.parentElement;
  }
  return null;
}
